import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { stagger, fadeUp } from '@/lib/animations';
import {
  Bell,
  AlertTriangle,
  AlertCircle,
  Info,
  MoreHorizontal,
  Eye,
  Check,
  X,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { PageHeader, DataTable, Column, MetricCard, KPIGrid } from '@/components/common';
import { alertsApi, Alert, QueryAlertParams } from '@/api/services';
import { getErrorMessage } from '@/api/client';
import { useAuth } from '@/context/AuthContext';
import { useAlerts } from '@/hooks/useAlerts';

const severityIcons: Record<string, React.ReactNode> = {
  CRITICAL: <AlertCircle className="h-4 w-4 text-danger-500" />,
  HIGH: <AlertTriangle className="h-4 w-4 text-warning-600" />,
  MEDIUM: <Info className="h-4 w-4 text-warning-500" />,
  LOW: <Info className="h-4 w-4 text-primary-500" />,
};

const severityColors: Record<string, string> = {
  CRITICAL: 'bg-danger-50 text-danger-600',
  HIGH: 'bg-warning-50 text-warning-600',
  MEDIUM: 'bg-warning-50 text-warning-600',
  LOW: 'bg-primary-50 text-primary-500',
};

// Helper to derive status from alert state
type AlertStatus = 'active' | 'read' | 'dismissed';
const getAlertStatus = (alert: Alert): AlertStatus => {
  if (alert.isDismissed) return 'dismissed';
  if (alert.isRead) return 'read';
  return 'active';
};

const statusColors: Record<AlertStatus, string> = {
  active: 'bg-danger-50 text-danger-600',
  read: 'bg-warning-50 text-warning-600',
  dismissed: 'bg-muted text-muted-foreground',
};

export default function Alerts() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [alerts, setAlerts] = useState<Alert[]>([]);
  const [loading, setLoading] = useState(true);
  const [severityFilter, setSeverityFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');

  // Real-time alerts via WebSocket
  const { latestAlert, setUnreadCount } = useAlerts(user?.companyId);

  const fetchAlerts = async (params?: QueryAlertParams) => {
    try {
      setLoading(true);
      const response = await alertsApi.getAll(params);
      setAlerts(response.data);
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  // Fetch alerts when filters change
  useEffect(() => {
    const params: QueryAlertParams = {};
    if (severityFilter !== 'all') {
      params.severity = severityFilter as Alert['severity'];
    }
    if (statusFilter === 'active') {
      params.isRead = false;
      params.isDismissed = false;
    } else if (statusFilter === 'read') {
      params.isRead = true;
      params.isDismissed = false;
    } else if (statusFilter === 'dismissed') {
      params.isDismissed = true;
    }
    fetchAlerts(params);
  }, [severityFilter, statusFilter]);

  // Handle new alerts from WebSocket
  useEffect(() => {
    if (latestAlert) {
      // Add the new alert to the list if it matches current filters
      const shouldAdd =
        (severityFilter === 'all' || latestAlert.severity === severityFilter) &&
        (statusFilter === 'all' ||
          (statusFilter === 'active' && !latestAlert.isRead && !latestAlert.isDismissed));

      if (shouldAdd) {
        setAlerts((prev) => [latestAlert, ...prev]);
      }
    }
  }, [latestAlert, severityFilter, statusFilter]);

  const handleAcknowledge = async (alert: Alert) => {
    try {
      await alertsApi.acknowledge(alert.id);
      toast.success('Alert acknowledged');

      // Update local state immediately for instant feedback
      setAlerts((prev) => prev.map((a) => (a.id === alert.id ? { ...a, isRead: true } : a)));

      // Decrement unread count if alert was not read
      if (!alert.isRead) {
        setUnreadCount((prev) => Math.max(0, prev - 1));
      }

      // WebSocket will also emit alert_updated event
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const handleDismiss = async (alert: Alert) => {
    try {
      await alertsApi.dismiss(alert.id);
      toast.success('Alert dismissed');

      // Update local state immediately for instant feedback
      setAlerts((prev) => prev.map((a) => (a.id === alert.id ? { ...a, isDismissed: true } : a)));

      // Decrement unread count if alert was not read/dismissed
      if (!alert.isRead && !alert.isDismissed) {
        setUnreadCount((prev) => Math.max(0, prev - 1));
      }

      // WebSocket will also emit alert_updated event
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Calculate metrics
  const criticalCount = alerts.filter(
    (a) => a.severity === 'CRITICAL' && getAlertStatus(a) === 'active'
  ).length;
  const highCount = alerts.filter(
    (a) => a.severity === 'HIGH' && getAlertStatus(a) === 'active'
  ).length;
  const activeCount = alerts.filter((a) => getAlertStatus(a) === 'active').length;
  const readCount = alerts.filter((a) => getAlertStatus(a) === 'read').length;

  const columns: Column<Alert>[] = [
    {
      key: 'title',
      linkTo: (alert) => (alert.itemId ? `/inventory/${alert.itemId}` : null),
      header: 'Alert',
      sortable: true,
      render: (alert) => (
        <div className="flex items-center gap-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-muted">
            {severityIcons[alert.severity]}
          </div>
          <div>
            <div className="font-medium">{alert.title}</div>
            <div className="text-sm text-muted-foreground line-clamp-1">{alert.message}</div>
          </div>
        </div>
      ),
    },
    {
      key: 'type',
      header: 'Type',
      render: (alert) => <Badge variant="outline">{alert.type.replace(/_/g, ' ')}</Badge>,
    },
    {
      key: 'severity',
      header: 'Severity',
      render: (alert) => <Badge className={severityColors[alert.severity]}>{alert.severity}</Badge>,
    },
    {
      key: 'status',
      header: 'Status',
      render: (alert) => {
        const status = getAlertStatus(alert);
        return <Badge className={statusColors[status]}>{status.toUpperCase()}</Badge>;
      },
    },
    {
      key: 'createdAt',
      header: 'Created',
      render: (alert) => (
        <span className="text-muted-foreground">{formatDate(alert.createdAt)}</span>
      ),
    },
    {
      key: 'actions',
      header: '',
      className: 'w-12',
      render: (alert) => (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" aria-label="More actions" className="h-8 w-8">
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {/* This rendered with no onClick, so it looked like a way in and
                did nothing. An alert is always about something — most carry the
                item they concern — so it now opens that record, and is hidden
                when there is nothing to open. */}
            {alert.itemId && (
              <DropdownMenuItem onClick={() => navigate(`/inventory/${alert.itemId}`)}>
                <Eye className="mr-2 h-4 w-4" />
                View item
              </DropdownMenuItem>
            )}
            {getAlertStatus(alert) === 'active' && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => handleAcknowledge(alert)}>
                  <Check className="mr-2 h-4 w-4" />
                  Acknowledge
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleDismiss(alert)}>
                  <X className="mr-2 h-4 w-4" />
                  Dismiss
                </DropdownMenuItem>
              </>
            )}
            {getAlertStatus(alert) === 'read' && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => handleDismiss(alert)}>
                  <X className="mr-2 h-4 w-4" />
                  Dismiss
                </DropdownMenuItem>
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      ),
    },
  ];

  return (
    <motion.main className="space-y-5" variants={stagger} initial="hidden" animate="show">
      <motion.div variants={fadeUp}>
        <PageHeader
          title="Alerts"
          description="Conditions the system has raised for attention, with severity and current status."
        />
      </motion.div>

      {/* KPI Cards */}
      <div className="mt-6">
        <KPIGrid columns={4}>
          <MetricCard
            label="Critical"
            value={criticalCount}
            sentiment={criticalCount > 0 ? 'danger' : 'success'}
            icon={<AlertCircle className="h-5 w-5" />}
          />
          <MetricCard
            label="High Priority"
            value={highCount}
            sentiment={highCount > 0 ? 'warning' : 'success'}
            icon={<AlertTriangle className="h-5 w-5" />}
          />
          <MetricCard
            label="Active Alerts"
            value={activeCount}
            sentiment={activeCount > 0 ? 'info' : 'success'}
            icon={<Bell className="h-5 w-5" />}
          />
          <MetricCard
            label="Read"
            value={readCount}
            sentiment="neutral"
            icon={<Check className="h-5 w-5" />}
          />
        </KPIGrid>
      </div>

      {/* Filters */}
      <motion.div variants={fadeUp} className="flex items-center gap-4 mt-6">
        <Select value={severityFilter} onValueChange={setSeverityFilter}>
          <SelectTrigger className="w-36">
            <SelectValue placeholder="Severity" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Severity</SelectItem>
            <SelectItem value="CRITICAL">Critical</SelectItem>
            <SelectItem value="HIGH">High</SelectItem>
            <SelectItem value="MEDIUM">Medium</SelectItem>
            <SelectItem value="LOW">Low</SelectItem>
          </SelectContent>
        </Select>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-36">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="read">Read</SelectItem>
            <SelectItem value="dismissed">Dismissed</SelectItem>
          </SelectContent>
        </Select>
      </motion.div>

      <motion.div variants={fadeUp} className="mt-6">
        <DataTable
          data={alerts}
          columns={columns}
          loading={loading}
          pagination
          exportable
          columnToggle
          searchable
          emptyTitle="No alerts found"
          emptyDescription="All systems are operating normally."
        />
      </motion.div>
    </motion.main>
  );
}
